import React, { useState } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    parseISO
} from 'date-fns';
import { id } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';

import { cn } from '../lib/utils'; // Assumed exist
import { STATUS } from '../lib/theme';

export function ExpiryCalendar({ certificates }) {
    const [currentDate, setCurrentDate] = useState(new Date());

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Senin
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const calendarDays = eachDayOfInterval({
        start: startDate,
        end: endDate,
    });

    const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const goToToday = () => setCurrentDate(new Date());

    // Function to get certs for a specific day
    const getCertsForDay = (day) => {
        return certificates.filter(cert => {
            // Assuming expiration_date is ISO string or Date object
            const expDate = typeof cert.expiration_date === 'string'
                ? parseISO(cert.expiration_date)
                : cert.expiration_date;
            return isSameDay(expDate, day);
        });
    };

    return (
        <Card className="h-full flex flex-col min-h-[400px]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 flex-shrink-0">
                <div className="space-y-1">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
                        Kalender Kedaluwarsa
                    </CardTitle>
                    <CardDescription>
                        Jadwal habis masa berlaku sertifikat
                    </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" onClick={prevMonth}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold w-32 text-center">
                        {format(currentDate, 'MMMM yyyy', { locale: id })}
                    </div>
                    <Button variant="outline" size="icon" onClick={nextMonth}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
                {/* Days Header */}
                <div className="grid grid-cols-7 mb-2 text-center text-sm text-muted-foreground font-medium">
                    {['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map(day => (
                        <div key={day} className="py-2">{day}</div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day, idx) => {
                        const certs = getCertsForDay(day);
                        const hasCerts = certs.length > 0;
                        const isSelectedMonth = isSameMonth(day, monthStart);
                        const isTodayDay = isSameDay(day, new Date());

                        return (
                            <Popover key={day.toString()}>
                                <PopoverTrigger asChild>
                                    <button
                                        disabled={!hasCerts && !isSelectedMonth} // Optional: disable interactions on empty days outside month
                                        className={cn(
                                            "h-14 sm:h-20 w-full rounded-sm border text-sm flex flex-col items-start justify-start p-1 sm:p-2 transition-colors relative hover:bg-accent",
                                            !isSelectedMonth && "text-muted-foreground bg-muted/20 border-muted/20"
                                        )}
                                        style={
                                            isTodayDay
                                                ? { background: STATUS.primary.bg, borderColor: STATUS.primary.border }
                                                : hasCerts
                                                    ? { background: STATUS.warning.bg, borderColor: STATUS.warning.border }
                                                    : undefined
                                        }
                                    >
                                        <span
                                            className="font-medium"
                                            style={isTodayDay ? { color: STATUS.primary.text } : undefined}
                                        >
                                            {format(day, 'd')}
                                        </span>

                                        {/* Indicators */}
                                        {hasCerts && (
                                            <div className="mt-auto w-full flex flex-wrap gap-1 content-end">
                                                <div
                                                    className="hidden sm:block text-xs text-left w-full truncate font-medium px-1 rounded-sm"
                                                    style={{ color: STATUS.warning.text, background: STATUS.warning.border }}
                                                >
                                                    {certs.length} Expired
                                                </div>
                                                {/* Dot for mobile */}
                                                <div
                                                    className="sm:hidden h-1.5 w-1.5 rounded-full mx-auto mb-1"
                                                    style={{ background: STATUS.critical.icon }}
                                                ></div>
                                            </div>
                                        )}
                                    </button>
                                </PopoverTrigger>

                                {hasCerts && (
                                    <PopoverContent className="w-64 p-3">
                                        <div className="space-y-2">
                                            <h4 className="font-semibold text-sm border-b pb-1 mb-2">
                                                {format(day, 'dd MMMM yyyy', { locale: id })}
                                            </h4>
                                            <div className="space-y-1">
                                                {certs.map(cert => (
                                                    <div key={cert.id} className="flex items-start gap-2 text-sm">
                                                        <AlertTriangle className="h-4 w-4 mt-0.5" style={{ color: STATUS.critical.icon }} />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium truncate">{cert.domain}</p>
                                                            <p className="text-xs text-muted-foreground">{cert.server_source}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </PopoverContent>
                                )}
                            </Popover>
                        );
                    })}
                </div>

                <div className="flex justify-end mt-4">
                    <Button variant="ghost" size="sm" onClick={goToToday}>Hari Ini</Button>
                </div>
            </CardContent>
        </Card>
    );
}
